import { useState, useEffect, useCallback } from 'react';
import { BrowseResult } from '../types/api';
import { api, ApiError } from '../services/api';

interface UseBrowseResult {
  data: BrowseResult | null;
  loading: boolean;
  error: string | null;
  currentPath: string;
  navigateToPath: (path: string) => void;
  navigateToParent: () => void;
  refresh: () => void;
}

export function useBrowse(initialPath: string = ''): UseBrowseResult {
  const [data, setData] = useState<BrowseResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState(initialPath);

  const fetchData = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await api.browse(path);
      setData(result);
      setCurrentPath(result.currentPath);
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to load media files';
      setError(errorMessage);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const navigateToPath = useCallback((path: string) => {
    fetchData(path);
  }, [fetchData]);

  const navigateToParent = useCallback(() => {
    const pathParts = currentPath.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      pathParts.pop();
      const parentPath = pathParts.join('/');
      fetchData(parentPath);
    }
  }, [currentPath, fetchData]);

  const refresh = useCallback(() => {
    fetchData(currentPath);
  }, [currentPath, fetchData]);

  // Initial load
  useEffect(() => {
    fetchData(initialPath);
  }, [initialPath, fetchData]);

  return {
    data,
    loading,
    error,
    currentPath,
    navigateToPath,
    navigateToParent,
    refresh,
  };
}